import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fetchAuthSession } from "aws-amplify/auth";
import { generateClient } from "aws-amplify/data";
import type { Schema } from "schema/data/resource";
import {
  BookIcon,
  FileIcon,
  EnvelopeIcon,
  CloudUploadIcon,
  PersonIcon,
} from "assets/icons";
import { Card, LoadingIndicator } from "components/atoms";
import { Breadcrumbs } from "components/molecules";
import { useBreadcrumb } from "contexts";
import { useUserData } from "hooks";
import "./DashboardPage.scss";

const client = generateClient<Schema>();

interface DashboardCard {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  route: string;
  allowedUserTypes: string[];
}

const dashboardCards: DashboardCard[] = [
  {
    id: "ingestion",
    title: "Course Ingestion",
    description: "Import and process course data from external sources",
    icon: <CloudUploadIcon />,
    route: "/courseIngestion",
    allowedUserTypes: ["EDUBAY"],
  },
  {
    id: "users",
    title: "Users Management",
    description:
      "Manage user accounts, roles, permissions, and access control across the platform",
    icon: <PersonIcon />,
    route: "/usersManagement",
    allowedUserTypes: ["EDUBAY", "PROVIDER", "PARTNER"],
  },
  {
    id: "institution",
    title: "Institution Management",
    description:
      "Manage institutions, monitor onboarding status, and oversee institutional relationships",
    icon: <PersonIcon />,
    route: "/institutionManagement",
    allowedUserTypes: ["EDUBAY"],
  },
  {
    id: "agent",
    title: "Agent Management",
    description:
      "Manage recruitment agents, approve partnerships, and track performance",
    icon: <PersonIcon />,
    route: "/agentManagement",
    allowedUserTypes: ["EDUBAY", "PROVIDER"],
  },
  {
    id: "application",
    title: "Application Management",
    description:
      "Process student applications, track status, and manage admissions workflow",
    icon: <FileIcon />,
    route: "/applicationManagement",
    allowedUserTypes: ["EDUBAY", "PROVIDER", "PARTNER"],
  },
  {
    id: "inquiry",
    title: "Enquiry Management",
    description:
      "View and manage student enquiries, track responses, and convert to applications",
    icon: <EnvelopeIcon />,
    route: "/enquiryManagement",
    allowedUserTypes: ["EDUBAY", "PROVIDER", "PARTNER"],
  },
  {
    id: "course",
    title: "Course Management",
    description:
      "Create and manage course listings, update requirements, and pricing",
    icon: <BookIcon />,
    route: "/courseManagement",
    allowedUserTypes: ["EDUBAY", "PROVIDER"],
  },
];

const colorClasses = ["primary", "secondary", "tertiary", "accent"];
const getColorClass = (index: number) =>
  colorClasses[index % colorClasses.length];

const DashboardPage: React.FC = () => {
  const { updateBreadcrumbs } = useBreadcrumb();
  const { userData } = useUserData();
  const navigate = useNavigate();
  const [userType, setUserType] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [cardOrder, setCardOrder] = useState<string[]>([]);
  const [editMode, setEditMode] = useState(false);
  const [draggedCard, setDraggedCard] = useState<string | null>(null);
  const [dragOverCard, setDragOverCard] = useState<string | null>(null);

  useEffect(() => {
    updateBreadcrumbs([{ path: "/management", label: "Dashboard" }]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const init = async () => {
      if (!userData?.email) {
        return;
      }

      try {
        const session = await fetchAuthSession();
        const groups = session.tokens?.accessToken?.payload[
          "cognito:groups"
        ] as string[] | undefined;

        if (groups?.includes("EDUBAY")) {
          setUserType("EDUBAY");
        } else if (groups?.includes("PROVIDER")) {
          setUserType("PROVIDER");
        } else if (groups?.includes("PARTNER")) {
          setUserType("PARTNER");
        }

        const token = session.tokens?.idToken?.toString();
        const { data } = await client.models.UserDashboardPreference.list({
          filter: { userId: { eq: userData.email } },
          authMode: "userPool",
          authToken: token,
        });

        if (data && data.length > 0 && data[0].cardOrder) {
          const cleanOrder = data[0].cardOrder.filter(
            (id): id is string => id !== null,
          );
          setCardOrder(cleanOrder);
        }

        setLoading(false);
      } catch (error) {
        console.error("Failed to initialize:", error);
        setLoading(false);
      }
    };

    init();
  }, [userData?.email]);

  const saveCardOrder = async (newOrder: string[]) => {
    if (!userData?.email) return;

    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();

      const { data: existing } =
        await client.models.UserDashboardPreference.list({
          filter: { userId: { eq: userData.email } },
          authMode: "userPool",
          authToken: token,
        });

      if (existing && existing.length > 0) {
        await client.models.UserDashboardPreference.update(
          { id: existing[0].id, cardOrder: newOrder },
          { authMode: "userPool", authToken: token },
        );
      } else {
        await client.models.UserDashboardPreference.create(
          { userId: userData.email, cardOrder: newOrder },
          { authMode: "userPool", authToken: token },
        );
      }
    } catch (error) {
      console.error("Failed to save card order:", error);
    }
  };

  const filteredCards = dashboardCards.filter((card) =>
    card.allowedUserTypes.includes(userType),
  );

  const orderedCards =
    cardOrder.length > 0
      ? cardOrder
          .map((id) => filteredCards.find((c) => c.id === id))
          .filter((c): c is DashboardCard => c !== undefined)
          .concat(filteredCards.filter((c) => !cardOrder.includes(c.id)))
      : filteredCards;

  const handleDragStart = (e: React.DragEvent, cardId: string) => {
    setDraggedCard(cardId);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/html", cardId);
  };

  const handleDragOver = (e: React.DragEvent, cardId: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverCard(cardId);
  };

  const handleDragLeave = () => {
    setDragOverCard(null);
  };

  const handleDrop = (e: React.DragEvent, targetCardId: string) => {
    e.preventDefault();

    if (!draggedCard || draggedCard === targetCardId) {
      setDraggedCard(null);
      setDragOverCard(null);
      return;
    }

    const draggedIndex = orderedCards.findIndex((c) => c.id === draggedCard);
    const targetIndex = orderedCards.findIndex((c) => c.id === targetCardId);

    if (draggedIndex === -1 || targetIndex === -1) {
      setDraggedCard(null);
      setDragOverCard(null);
      return;
    }

    const newOrder = [...orderedCards];
    const [moved] = newOrder.splice(draggedIndex, 1);
    newOrder.splice(targetIndex, 0, moved);

    const newCardOrder = newOrder.map((c) => c.id);
    setCardOrder(newCardOrder);
    saveCardOrder(newCardOrder);

    setDraggedCard(null);
    setDragOverCard(null);
  };

  const handleDragEnd = () => {
    setDraggedCard(null);
    setDragOverCard(null);
  };

  const handleCardClick = (route: string) => {
    if (!editMode) {
      navigate(route);
    }
  };

  if (loading) {
    return <LoadingIndicator />;
  }

  return (
    <section className="management-dashboard-container">
      <div className="container">
        <Breadcrumbs />

        <div className="dashboard-header">
          <h1>Management Dashboard</h1>
          <button
            className="edit-button"
            onClick={() => setEditMode(!editMode)}
          >
            {editMode ? "Done" : "Customize"}
          </button>
        </div>

        {filteredCards.length === 0 ? (
          <Card
            className="no-access-card"
            description={
              <div className="no-access-content">
                <h3>No Management Access</h3>
                <p>You don't have access to any management modules.</p>
              </div>
            }
          />
        ) : (
          <>
            {editMode && (
              <div className="edit-instructions">
                Drag and drop cards to reorder
              </div>
            )}

            <div className="dashboard-grid">
              {orderedCards.map((card, index) => (
                <div
                  key={card.id}
                  draggable={editMode}
                  onDragStart={(e) => handleDragStart(e, card.id)}
                  onDragOver={(e) => handleDragOver(e, card.id)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, card.id)}
                  onDragEnd={handleDragEnd}
                  className={`card-wrapper ${
                    draggedCard === card.id ? "dragging" : ""
                  } ${dragOverCard === card.id ? "drag-over" : ""}`}
                >
                  <Card
                    className={`dashboard-module-card color-${getColorClass(index)}`}
                    description={
                      <div className="module-card-content">
                        {editMode && <div className="drag-handle">⋮⋮</div>}
                        <div onClick={() => handleCardClick(card.route)}>
                          <div className="card-icon">{card.icon}</div>
                          <h3>{card.title}</h3>
                          <p>{card.description}</p>
                        </div>
                      </div>
                    }
                    footerContent={
                      <div
                        className="card-action"
                        onClick={() => handleCardClick(card.route)}
                      >
                        <span className="card-link">Manage →</span>
                      </div>
                    }
                  />
                </div>
              ))}
            </div>

            {userType === "EDUBAY" && (
              <Card
                className="admin-info-card"
                title="System Administrator Tools"
                description={
                  <div className="admin-info">
                    <p>
                      Full access to all management modules across all
                      institutions.
                    </p>
                  </div>
                }
              />
            )}

            {userType === "PROVIDER" && (
              <Card
                className="provider-info-card"
                title="Institution Management"
                description={
                  <div className="provider-info">
                    <p>
                      Manage your institution's enquiries, applications, agents,
                      and courses.
                    </p>
                  </div>
                }
              />
            )}
          </>
        )}
      </div>
    </section>
  );
};

export default DashboardPage;
